export const BOOK_SIZE_OPTIONS = [
  'lulu_6x9_softcover',
  'lulu_6x9_hardcover',
] as const;

export type BookSize = (typeof BOOK_SIZE_OPTIONS)[number];

export const DEFAULT_BOOK_SIZE: BookSize = 'lulu_6x9_softcover';

export const BOOK_SIZE_DPI = 300;

export interface BookSizeDimensions {
  page: {
    widthInches: number;
    heightInches: number;
    /** Width in points (72pt = 1 inch) */
    widthPt: number;
    /** Height in points (72pt = 1 inch) */
    heightPt: number;
  };
  cover: {
    widthInches: number;
    heightInches: number;
    /** Width in points (72pt = 1 inch) */
    widthPt: number;
    /** Height in points (72pt = 1 inch) */
    heightPt: number;
  };
  /**
   * Wrap+bleed margin (inches) reserved on the front-cover slot's top,
   * bottom, and outer (right) edges of the wrap-around cover. On hardcover
   * casewrap binding the outer ~0.75″ wraps around the board and is glued
   * inside, so primary content (faces, title, character) must stay outside
   * this band. Softcover saddle-stitch has no wrap (margin = 0).
   *
   * Lulu spec: 0.75″ wrap + 0.125″ bleed = 0.875″. Issue #708.
   */
  coverWrapMarginInches: number;
}

/** Convert points to inches (72pt = 1 inch) */
export const pointsToInches = (pt: number): number => pt / 72;

/** Convert inches to points (72pt = 1 inch) */
export const inchesToPoints = (inches: number): number => inches * 72;

/**
 * PDF-ready dimensions for each supported book size (Lulu.com 6×9 format).
 * Dimensions provided in both points (for PDF generation) and inches (for print specs).
 *
 * Page: 6.25 × 9.25 in = 450pt × 666pt
 * Softcover cover: 12.25 × 9.25 in = 882pt × 666pt (from Lulu pricing calculator; used as fallback when Lulu API is unavailable)
 * Hardcover cover: 14 × 10.75 in = 1008pt × 774pt
 */
export const BOOK_SIZE_DIMENSIONS: Record<BookSize, BookSizeDimensions> = {
  lulu_6x9_softcover: {
    page: {
      widthPt: 450,
      heightPt: 666,
      widthInches: pointsToInches(450),
      heightInches: pointsToInches(666),
    },
    cover: {
      widthPt: 882,
      heightPt: 666,
      widthInches: pointsToInches(882),
      heightInches: pointsToInches(666),
    },
    // Saddle-stitch softcover has no board wrap — only the standard 0.125″
    // bleed handled by the outpaint pipeline's existing edge guards.
    coverWrapMarginInches: 0,
  },
  lulu_6x9_hardcover: {
    page: {
      widthPt: 450,
      heightPt: 666,
      widthInches: pointsToInches(450),
      heightInches: pointsToInches(666),
    },
    cover: {
      widthPt: 1008,
      heightPt: 774,
      widthInches: pointsToInches(1008),
      heightInches: pointsToInches(774),
    },
    // Casewrap binding: 0.75″ wrap (folds around the board) + 0.125″ bleed.
    // Issue #708 Part 2 — reserves a 4-sided wrap frame on the outpaint
    // canvas so primary content stays inside the visible 6×9″ face.
    coverWrapMarginInches: 0.875,
  },
};
